// Single source of truth for client-side routes.
// Never inline a route string; import from here.

export const HOSTS = {
  marketing: "motiftrace.com",
  app: "app.motiftrace.com",
  api: "api.motiftrace.com",
  collect: "collect.motiftrace.com",
  docs: "docs.motiftrace.com",
  /** First-party PostHog ingest (ad-blocker bypass). Worker `posthog-proxy`. */
  ingest: "e.motiftrace.com",
} as const;

/** Motif's own marketing apex in the Motif app. The worker injects the
 *  tracker on this host only so app. and docs. do not pollute the cohort. */
export const MARKETING_WEBSITE_ID = "b5813927-f713-4473-8b34-9f719b645560";

/** Public support inbox. Import this; do not retype the address. */
export const SUPPORT_EMAIL = "joseph@motiftrace.com";

export function isLocalHost(hostname: string): boolean {
  const h = hostname.toLowerCase();
  return h === "localhost" || h === "127.0.0.1";
}

function routePath(path: string): string {
  return path.startsWith("/") ? path : `/${path}`;
}

/** Shared tracker host. Local stays on the vite/API origin. */
export function defaultTrackerOrigin(hostname: string, localOrigin: string): string {
  if (isLocalHost(hostname)) {
    return localOrigin;
  }
  return `https://${HOSTS.collect}`;
}

/** The one tracker tag. It names the site's first-party host only after
 *  Motif verified that host serves Motif's m.js; until then it loads from
 *  Motif's collector. An unverified m.<domain> can be a mobile site, an SPA
 *  catch-all, or a dangling CNAME someone else controls, and the tag runs
 *  whatever that host returns on every page of the customer's site. */
export function trackerTag(
  site: { id: string; domain: string; tracking_host?: string | null; tracking_host_status: string },
  collectorOrigin: string,
): string {
  const origin = site.tracking_host_status === "active" && site.tracking_host
    ? `https://${site.tracking_host}`
    : collectorOrigin.replace(/\/$/, "");
  return `<script defer src="${origin}/m.js" data-website-id="${site.id}" data-domain="${site.domain}"></script>`;
}

export function isMarketingHost(hostname = typeof window === "undefined" ? "" : window.location.hostname): boolean {
  const h = hostname.toLowerCase();
  return h === HOSTS.marketing || h === `www.${HOSTS.marketing}`;
}

export function isDocsHost(hostname = typeof window === "undefined" ? "" : window.location.hostname): boolean {
  return hostname.toLowerCase() === HOSTS.docs;
}

/** Marketing + docs are public/indexable. Unknown and app hosts are not. */
export function isPublicHost(hostname = typeof window === "undefined" ? "" : window.location.hostname): boolean {
  return isMarketingHost(hostname) || isDocsHost(hostname);
}

/** App origin for sign-in/sign-up. Relative on localhost and the app host. */
export function appURLFor(path: string, hostname: string): string {
  const p = routePath(path);
  if (isLocalHost(hostname) || hostname.toLowerCase() === HOSTS.app) {
    return p;
  }
  return `https://${HOSTS.app}${p}`;
}

export function appURL(path: string): string {
  const hostname = typeof window === "undefined" ? HOSTS.marketing : window.location.hostname;
  return appURLFor(path, hostname);
}

/** Marketing origin for public pages and post-sign-out navigation. */
export function marketingURLFor(path: string, hostname: string): string {
  const p = routePath(path);
  if (isLocalHost(hostname) || isMarketingHost(hostname)) {
    return p;
  }
  return `https://${HOSTS.marketing}${p}`;
}

export function marketingURL(path: string): string {
  const hostname = typeof window === "undefined" ? HOSTS.app : window.location.hostname;
  return marketingURLFor(path, hostname);
}

/** Absolute marketing URL. Docs chrome always points at the public site. */
export function marketingAbsoluteURL(path: string): string {
  const p = routePath(path);
  return `https://${HOSTS.marketing}${p === "/" ? "/" : p}`;
}

/** Streamable HTTP MCP endpoint. Local uses the API origin. */
export function mcpURL(hostname: string, localOrigin: string): string {
  if (isLocalHost(hostname)) {
    return `${localOrigin}/mcp`;
  }
  return `https://${HOSTS.api}/mcp`;
}

/** POST target for server milestones, sent from a customer's backend. */
export function milestonesURL(hostname: string, localOrigin: string): string {
  if (isLocalHost(hostname)) {
    return `${localOrigin}/api/v1/milestones`;
  }
  return `https://${HOSTS.api}/api/v1/milestones`;
}

export function docsURL(path: string = "/"): string {
  const p = routePath(path);
  return `https://${HOSTS.docs}${p === "/" ? "/" : p}`;
}

/** AI crawler directory. Hub plus one document per vendor token Motif names,
 *  plus robots.txt product tokens that are not HTTP User-Agents. */
export const CRAWLER_ROUTES = {
  HUB: "/crawlers",
  GPTBOT: "/crawlers/gptbot",
  CHATGPT_USER: "/crawlers/chatgpt-user",
  OAI_SEARCHBOT: "/crawlers/oai-searchbot",
  CLAUDEBOT: "/crawlers/claudebot",
  CLAUDE_USER: "/crawlers/claude-user",
  CLAUDE_SEARCHBOT: "/crawlers/claude-searchbot",
  ANTHROPIC_AI: "/crawlers/anthropic-ai",
  PERPLEXITYBOT: "/crawlers/perplexitybot",
  PERPLEXITY_USER: "/crawlers/perplexity-user",
  META_EXTERNALAGENT: "/crawlers/meta-externalagent",
  META_EXTERNALFETCHER: "/crawlers/meta-externalfetcher",
  META_WEBINDEXER: "/crawlers/meta-webindexer",
  GOOGLE_CLOUDVERTEXBOT: "/crawlers/google-cloudvertexbot",
  AMAZONBOT: "/crawlers/amazonbot",
  AMZN_USER: "/crawlers/amzn-user",
  AMZN_SEARCHBOT: "/crawlers/amzn-searchbot",
  BYTESPIDER: "/crawlers/bytespider",
  CCBOT: "/crawlers/ccbot",
  GOOGLE_EXTENDED: "/crawlers/google-extended",
  APPLEBOT_EXTENDED: "/crawlers/applebot-extended",
} as const;

export const GEO_ROUTES = {
  HUB: "/geo",
  VS_SEO: "/geo/vs-seo",
  MEASURE: "/geo/measure",
  CITATIONS: "/geo/citations",
  LLMS_TXT: "/geo/llms-txt",
  SCHEMA: "/geo/schema",
  PLAYBOOK: "/geo/playbook",
  OVERVIEWS: "/geo/overviews",
} as const;

export const TOOL_ROUTES = {
  HUB: "/tools",
  UTM_BUILDER: "/tools/utm-builder",
  UTM_CHECKER: "/tools/utm-checker",
  ROBOTS_TXT: "/tools/robots-txt",
  REFERRER: "/tools/referrer",
  EXTENSION: "/tools/extension",
} as const;

/** Ungated paste-URL scan. Host reports live at /check/{registrableHost}. */
export const CHECK_ROUTES = {
  HUB: "/check",
} as const;

export const RESEARCH_ROUTES = {
  HUB: "/research",
  METHODOLOGY: "/research/methodology",
  AI_REFERRAL: "/research/ai-referral",
  CRAWLER_OBSERVATION: "/research/crawler-observation",
} as const;

export const ROUTES = {
  HOME: "/",
  PRICING: "/pricing",
  PRIVACY: "/privacy",
  TERMS: "/terms",
  ABOUT: "/about",
  HOW_IT_WORKS: "/how-it-works",
  FIRST_TOUCH: "/first-touch",
  STRIPE: "/stripe",
  SEARCH_CONSOLE: "/search-console",
  WEEKLY_DIGEST: "/weekly-digest",
  CHATGPT_TRAFFIC: "/chatgpt-traffic",
  CLAUDE_TRAFFIC: "/claude-traffic",
  PERPLEXITY_TRAFFIC: "/perplexity-traffic",
  GEMINI_TRAFFIC: "/gemini-traffic",
  COPILOT_TRAFFIC: "/copilot-traffic",
  AI_CRAWLERS: "/ai-crawlers",
  CRAWLERS: CRAWLER_ROUTES.HUB,
  GEO: GEO_ROUTES.HUB,
  TOOLS: TOOL_ROUTES.HUB,
  CHECK: CHECK_ROUTES.HUB,
  RESEARCH: RESEARCH_ROUTES.HUB,
  SAAS_ANALYTICS: "/saas-analytics",
  ACQUISITION_ANALYTICS: "/acquisition-analytics",
  VS_GOOGLE_ANALYTICS: "/vs/google-analytics",
  VS_PLAUSIBLE: "/vs/plausible",
  VS_MIXPANEL: "/vs/mixpanel",
  VS_AMPLITUDE: "/vs/amplitude",
  VS_POSTHOG: "/vs/posthog",
  VS_FATHOM: "/vs/fathom",
  FIRST_TOUCH_VS_LAST_CLICK: "/first-touch-vs-last-click",
  LLMS: "/llms.txt",
  /** Marketing/app path that 301s onto the docs host. Local Vite mounts Docs here. */
  DOCS_BRIDGE: "/docs",
  SIGN_IN: "/sign-in",
  SIGN_UP: "/sign-up",
  ACCOUNT: "/account",
  CONNECT: "/connect",
  WEBSITES: "/websites",
  INSIGHTS: "/insights",
  SEARCH: "/search",
  SEO: "/seo",
  CHANNELS: "/channels",
  // Retired pages, kept only for their redirect routes in App.tsx.
  REVENUE: "/revenue",
  SETTINGS: "/settings",
  SHARE: "/share/:token",
} as const;

/** Public share URL prefix. Not a catalog page; robots disallow this tree. */
export const SHARE_PATH_PREFIX = "/share/";

export function isSharePath(pathname: string): boolean {
  return pathname === "/share" || pathname.startsWith(SHARE_PATH_PREFIX);
}

/** High-intent search aliases from the 7 Sep 2026 keyword report. 301 onto
 *  one catalog URL. Do not mint a page per synonym. */
export const SEARCH_INTENT_ALIASES: Record<string, string> = {
  "/how-to-track-chatgpt-traffic": ROUTES.CHATGPT_TRAFFIC,
  "/track-chatgpt-traffic": ROUTES.CHATGPT_TRAFFIC,
  "/chatgpt-referral-traffic": ROUTES.CHATGPT_TRAFFIC,
  "/chatgpt-analytics": ROUTES.CHATGPT_TRAFFIC,
  "/track-chatgpt-traffic-ga4": ROUTES.CHATGPT_TRAFFIC,
  "/track-chatgpt-traffic-without-ga4": ROUTES.CHATGPT_TRAFFIC,
  "/chatgpt-traffic-showing-as-direct": ROUTES.CHATGPT_TRAFFIC,
  "/ga4-ai-assistant-channel": ROUTES.CHATGPT_TRAFFIC,
  "/ai-referral-traffic": RESEARCH_ROUTES.AI_REFERRAL,
  "/ai-traffic-analytics": RESEARCH_ROUTES.AI_REFERRAL,
  "/track-ai-traffic": RESEARCH_ROUTES.AI_REFERRAL,
  "/dark-ai-traffic": RESEARCH_ROUTES.AI_REFERRAL,
  "/best-analytics-for-ai-traffic": RESEARCH_ROUTES.AI_REFERRAL,
  "/track-claude-traffic": ROUTES.CLAUDE_TRAFFIC,
  "/claude-referral-traffic": ROUTES.CLAUDE_TRAFFIC,
  "/track-perplexity-traffic": ROUTES.PERPLEXITY_TRAFFIC,
  "/perplexity-referral-traffic": ROUTES.PERPLEXITY_TRAFFIC,
  "/track-gemini-traffic": ROUTES.GEMINI_TRAFFIC,
  "/gemini-referral-traffic": ROUTES.GEMINI_TRAFFIC,
  "/google-ai-mode-traffic": ROUTES.GEMINI_TRAFFIC,
  "/track-copilot-traffic": ROUTES.COPILOT_TRAFFIC,
  "/copilot-referral-traffic": ROUTES.COPILOT_TRAFFIC,
  "/first-touch-attribution": ROUTES.FIRST_TOUCH,
  "/last-click-attribution-model": ROUTES.FIRST_TOUCH_VS_LAST_CLICK,
  "/stripe-attribution": ROUTES.STRIPE,
  "/saas-acquisition-analytics": ROUTES.SAAS_ANALYTICS,
  "/saas-revenue-attribution": ROUTES.SAAS_ANALYTICS,
  "/how-to-measure-geo": GEO_ROUTES.MEASURE,
  "/geo-measurement-tool": GEO_ROUTES.MEASURE,
  "/motif-analytics": ROUTES.PRICING,
};

/** Keyword aliases that 301 onto a canonical catalog URL. */
export const MARKETING_REDIRECTS: Record<string, string> = {
  "/aeo": GEO_ROUTES.HUB,
  "/generative-engine-optimization": GEO_ROUTES.HUB,
  "/generative-engine-optimisation": GEO_ROUTES.HUB,
  "/answer-engine-optimization": GEO_ROUTES.HUB,
  "/answer-engine-optimisation": GEO_ROUTES.HUB,
  "/geo-guide": GEO_ROUTES.HUB,
  "/ai-bots": CRAWLER_ROUTES.HUB,
  "/ai-crawler-directory": CRAWLER_ROUTES.HUB,
  "/utm": TOOL_ROUTES.HUB,
  "/utm-builder": TOOL_ROUTES.UTM_BUILDER,
  "/free-tools": TOOL_ROUTES.HUB,
  ...SEARCH_INTENT_ALIASES,
};

export function marketingRedirectTarget(pathname: string): string | null {
  const path = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  return MARKETING_REDIRECTS[path] ?? null;
}

/** Public marketing HTML documents. App host signed-out traffic redirects here.
 *  Home is excluded: app.motiftrace.com/ is the dashboard. */
export const MARKETING_PUBLIC_PATHS = [
  ROUTES.PRICING,
  ROUTES.PRIVACY,
  ROUTES.TERMS,
  ROUTES.ABOUT,
  ROUTES.HOW_IT_WORKS,
  ROUTES.FIRST_TOUCH,
  ROUTES.STRIPE,
  ROUTES.SEARCH_CONSOLE,
  ROUTES.WEEKLY_DIGEST,
  ROUTES.CHATGPT_TRAFFIC,
  ROUTES.CLAUDE_TRAFFIC,
  ROUTES.PERPLEXITY_TRAFFIC,
  ROUTES.GEMINI_TRAFFIC,
  ROUTES.COPILOT_TRAFFIC,
  ROUTES.AI_CRAWLERS,
  ROUTES.SAAS_ANALYTICS,
  ROUTES.ACQUISITION_ANALYTICS,
  ROUTES.VS_GOOGLE_ANALYTICS,
  ROUTES.VS_PLAUSIBLE,
  ROUTES.VS_MIXPANEL,
  ROUTES.VS_AMPLITUDE,
  ROUTES.VS_POSTHOG,
  ROUTES.VS_FATHOM,
  ROUTES.FIRST_TOUCH_VS_LAST_CLICK,
  ...Object.values(CRAWLER_ROUTES),
  ...Object.values(GEO_ROUTES),
  ...Object.values(TOOL_ROUTES),
  ...Object.values(RESEARCH_ROUTES),
  CHECK_ROUTES.HUB,
] as const;

/** `/check` or `/check/{host}`. Host reports are shareable and not catalog pages. */
export function isCheckPath(pathname: string): boolean {
  const path = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  return path === CHECK_ROUTES.HUB || path.startsWith(`${CHECK_ROUTES.HUB}/`);
}

export function checkPathHost(pathname: string): string | null {
  const path = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  if (path === CHECK_ROUTES.HUB) return "";
  const prefix = `${CHECK_ROUTES.HUB}/`;
  if (!path.startsWith(prefix)) return null;
  return path.slice(prefix.length);
}

export function checkReportPath(host: string): string {
  return `${CHECK_ROUTES.HUB}/${host}`;
}

/** Mirrors domain.ParseScanURL: trim, https when the scheme is missing, http(s) only. */
export function publicScanTarget(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed || /\s/.test(trimmed)) return null;
  const lower = trimmed.toLowerCase();
  if (lower.startsWith("file:") || lower.startsWith("data:")) return null;
  const withScheme = trimmed.includes("://") ? trimmed : `https://${trimmed}`;
  try {
    const parsed = new URL(withScheme);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
    if (!parsed.hostname) return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

export const CHECK_SIGNUP_UTM = {
  utm_source: "motif-check",
  utm_medium: "site",
  utm_campaign: "url-scan",
} as const;

export function checkSignupHref(host: string, content?: string): string {
  const q = new URLSearchParams({
    website_hint: host,
    utm_source: CHECK_SIGNUP_UTM.utm_source,
    utm_medium: CHECK_SIGNUP_UTM.utm_medium,
    utm_campaign: CHECK_SIGNUP_UTM.utm_campaign,
  });
  if (content) q.set("utm_content", content);
  return `${appURL(ROUTES.SIGN_UP)}?${q.toString()}`;
}

/** Hostname shown in the scan theater. www is dropped so the brand reads as the site. */
export function scanDisplayHost(raw: string): string {
  const target = publicScanTarget(raw);
  try {
    return new URL(target ?? `https://${raw.trim()}`).hostname.replace(/^www\./i, "");
  } catch {
    return raw.trim();
  }
}

export function checkSignInHref(): string {
  return appURL(ROUTES.SIGN_IN);
}

export function canonicalMarketingRoute(pathname: string): string | null {
  return (MARKETING_PUBLIC_PATHS as readonly string[]).includes(pathname) ? pathname : null;
}

/** Catalog URLs the Motif Search HowTo campaign (and its URL expansion) can send.
 *  First-screen sign-up lives on these pages only. */
export const PAID_HOWTO_LANDINGS = [
  ROUTES.CHATGPT_TRAFFIC,
  ROUTES.CLAUDE_TRAFFIC,
  ROUTES.PERPLEXITY_TRAFFIC,
  ROUTES.GEMINI_TRAFFIC,
  ROUTES.COPILOT_TRAFFIC,
  RESEARCH_ROUTES.AI_REFERRAL,
] as const;

export function isPaidHowtoLanding(pathname: string): boolean {
  return (PAID_HOWTO_LANDINGS as readonly string[]).includes(pathname);
}

/** Paths on docs.motiftrace.com (root is the hub). */
export const DOCS_ROUTES = {
  HUB: "/",
  START_NONTECHNICAL: "/start/non-technical",
  START_DEVELOPERS: "/start/developers",
  START_FOUNDERS: "/start/founders",
  INSTALL: "/install",
  INSTALL_VERIFY: "/install/verify",
  INSTALL_FIRST_PARTY: "/install/first-party",
  STRIPE: "/stripe",
  STRIPE_SUCCESS: "/stripe/success-url",
  CONCEPTS_FIRST_TOUCH: "/concepts/first-touch",
  CONCEPTS_DIRECT: "/concepts/direct-unknown",
  CONCEPTS_SEARCH_CONSOLE: "/concepts/search-console",
  CONCEPTS_MCP: "/concepts/mcp",
} as const;

export function isLocalDocsPath(pathname: string): boolean {
  return pathname === ROUTES.DOCS_BRIDGE || pathname.startsWith(`${ROUTES.DOCS_BRIDGE}/`);
}

/** Strip `/docs` when Motif is served from localhost so the same catalog paths work. */
export function docsRouterBasename(hostname: string, pathname: string): string | undefined {
  if (isDocsHost(hostname)) return undefined;
  if (isLocalHost(hostname) && isLocalDocsPath(pathname)) return ROUTES.DOCS_BRIDGE;
  return undefined;
}

/** Marketing or app `/docs/...` -> docs host path. */
export function docsBridgeTargetPath(pathname: string): string | null {
  if (pathname === ROUTES.DOCS_BRIDGE || pathname === `${ROUTES.DOCS_BRIDGE}/`) return DOCS_ROUTES.HUB;
  if (pathname.startsWith(`${ROUTES.DOCS_BRIDGE}/`)) return pathname.slice(ROUTES.DOCS_BRIDGE.length);
  return null;
}

/** Dedicated Search Console workspace. Overview Google-organic and Websites
 *  both land here. */
export function searchConsoleEvidencePath(): string {
  return ROUTES.SEARCH;
}

/** Pages tab, Unindexed filter. Built from ROUTES.SEARCH so the query string
 *  is not a second route literal. */
export function searchUnindexedPath(): string {
  return `${ROUTES.SEARCH}?tab=pages&filter=unindexed`;
}
